import { useState } from 'react';

import {
  AddPreviousCampaignToDealMutation,
  AddPreviousCampaignToDealMutationVariables,
  DatasourceInstructionMode,
  AddPreviousCampaignToDealDocument,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { dealItemsRefetchState } from '@/states/products.state';
import { DomainError } from '@/types/data-source';
import { MutationHookOptions, useMutation } from '@apollo/client/react';
import { getDefaultStore } from 'jotai';

import { useDealNotEditableHandler } from './useDealNotEditableHandler';

export const useAddPreviousCampaignToDeal = (
  options?: Omit<
    MutationHookOptions<
      AddPreviousCampaignToDealMutation,
      AddPreviousCampaignToDealMutationVariables
    >,
    'variables'
  >
) => {
  const [domainError, setDomainError] = useState<DomainError | null>(null);
  const { handleIfDealNotEditable } = useDealNotEditableHandler();
  const [addPreviousCampaignToDealMutation, { loading, error }] = useMutation(
    AddPreviousCampaignToDealDocument,
    {
      ...(options ?? {}),
    }
  );

  const addPreviousCampaignToDeal = async (
    dealId: string,
    saleId: string,
    instructionMode?: DatasourceInstructionMode
  ): Promise<{ __typename?: 'MutationSuccess'; id: string } | undefined> => {
    setDomainError(null);
    trackEvent('IMPORT_STARTED', { dealId, source: 'previousCampaign' });

    const result = await addPreviousCampaignToDealMutation({
      variables: { dealId, saleId, instructionMode },
    });

    const mutationResult = result.data?.addPreviousCampaignToDeal;

    if (mutationResult?.__typename === 'MutationSuccess') {
      trackEvent('IMPORT_COMPLETED', {
        dealId,
        source: 'previousCampaign',
      });
      void getDefaultStore().get(dealItemsRefetchState)?.fn();
      return mutationResult;
    }
    if (mutationResult?.__typename === 'DomainException') {
      const errorCode = mutationResult.code;
      trackEvent('IMPORT_FAILED', {
        dealId,
        source: 'previousCampaign',
        errorCode,
      });
      if (handleIfDealNotEditable(errorCode)) return undefined;
      setDomainError({
        code: errorCode,
        message: `${errorCode}`,
      });
      return undefined;
    }
    trackEvent('IMPORT_FAILED', {
      dealId,
      source: 'previousCampaign',
      errorCode: 'IMPORT_FAILED',
    });
    setDomainError({
      code: 'IMPORT_FAILED',
      message: 'Unknown mutation result',
    });
    return undefined;
  };

  return {
    addPreviousCampaignToDeal,
    loading,
    error,
    domainError,
    isDomainError: Boolean(domainError),
  };
};
